import { addHours, format, isValid, startOfHour } from "date-fns";
import { ISTATUS } from "../../models/common/CommonModels";
import type {
  ForecastAttributeValue,
  ForecastClassBlock,
  RowData,
  TimeSlot,
} from "../../models/weather/IWeatherForecast";
import { AnchorAt } from "./WeatherConstants";

export interface BuildLabelsOptions {
  hours?: number;
  anchorAt?: AnchorAt;
  hourFormat?: string;
  anchorFormat?: string;
}

export interface TimeSlotMeta {
  label: string;
  date: Date;
  isMidnight: boolean; // hour==0
  isAnchor: boolean; // based on anchorAt
}

export function normalizeChildLabel(child: {
  attributeName?: string;
  condition?: string;
}): string {
  return child.attributeName ?? child.condition ?? "";
}

export function formatValueWithUnit(
  value: string | number,
  unit?: string
): string | number {
  if (value === "" || value === null || value === undefined) {
    return "";
  }
  return unit ? `${value} ${unit}` : value;
}

/** Ensure array matches target length by trimming or padding with a value */
export function normalizeLength<T>(
  arr: T[] | undefined,
  targetLen: number,
  padValue: T
): T[] {
  const out = (arr ?? []).slice(0, targetLen);
  while (out.length < targetLen) {
    out.push(padValue);
  }
  return out;
}

/**
 * Build hierarchical rows (tree) from props.
 * - Group rows keep groupStatus for the **Class** cell
 * - Child rows produce valueCells[] and slotStatuses[] aligned to timeSlots
 */
export function buildForecastData(
  timeSlots: TimeSlot[],
  input: ForecastClassBlock[]
): RowData[] {
  const groups: RowData[] = [];
  const slotCount = timeSlots.length;

  (input ?? []).forEach((grp) => {
    const subRows: RowData[] = (grp.attributes ?? []).map((child) => {
      const label = normalizeChildLabel(child);

      const values = child.values ?? [];

      const formattedValues = values.map(
        (vp: ForecastAttributeValue) => vp?.value as any
      );

      const statuses = values.map(
        (vp: ForecastAttributeValue) => vp?.status as ISTATUS
      );

      const alignedValues = normalizeLength<string | number>(
        formattedValues,
        slotCount,
        ""
      );
      const alignedStatuses = normalizeLength<ISTATUS>(statuses, slotCount, "");

      return {
        condition: label,
        unit: child.unit,
        valueCells: alignedValues,
        slotStatuses: alignedStatuses,
      };
    });

    groups.push({
      classLabel: grp.className,
      groupStatus: grp.status,
      subRows,
    });
  });
  return groups;
}

/**
 * Generates single-row table header labels for the next N hours from a local Date.
 * - Most labels: "HH:mm"
 * - Anchor labels (depending on anchorAt): "EEE HH:mm" (weekday + time)
 *
 * NOTE: Always pass a local Date string and not UTC string.
 * This function floors to the start of the hour (21:10 -> 21:00).
 */
export function buildNextHourHeaderLabels(
  startDateTime: Date,
  opts: BuildLabelsOptions = {}
): string[] {
  const {
    hours = 72,
    anchorAt = "all",
    hourFormat = "HH:mm",
    anchorFormat = "EEE HH:mm",
  } = opts;

  if (!isValid(startDateTime)) {
    return [];
  }

  const start = startOfHour(startDateTime);

  return Array.from({ length: hours }, (_, i) => {
    const nextHour = addHours(start, i);

    // Using date-fns format to derive hour-of-day in local time
    const hourOfDay = Number(format(nextHour, "H")); // 0..23

    const shouldAnchor =
      anchorAt === "all"
        ? true
        : anchorAt === "midnight"
        ? hourOfDay === 0
        : anchorAt === "every12hours"
        ? hourOfDay % 12 === 0
        : hourOfDay % 6 === 0;

    return shouldAnchor
      ? format(nextHour, anchorFormat)
      : format(nextHour, hourFormat);
  });
}
